import { TreeNode } from 'primeng/api';
import { Procedure } from '../../../../core/models/procedure.model';

export interface ProcedureTreeNodeData {
  levelType: 'family' | 'subfamily' | 'group' | 'subgroup' | 'procedure';
  label: string;
  procedureCount: number;
  procedure?: Procedure;
}

interface GroupNode {
  key: string;
  levelType: ProcedureTreeNodeData['levelType'];
  label: string;
  children: Map<string, GroupNode>;
  procedures: Procedure[];
}

function getOrCreateChild(
  parent: GroupNode,
  childKey: string,
  levelType: ProcedureTreeNodeData['levelType'],
  label: string
): GroupNode {
  let child = parent.children.get(childKey);
  if (!child) {
    child = { key: `${parent.key}/${childKey}`, levelType, label, children: new Map(), procedures: [] };
    parent.children.set(childKey, child);
  }
  return child;
}

function compareInternalCode(a: string, b: string): number {
  // Letter-prefixed codes (A1., A5., …) sort before digit-prefixed ones (08.*).
  const aIsDigit = /^\d/.test(a);
  const bIsDigit = /^\d/.test(b);
  if (aIsDigit !== bIsDigit) return aIsDigit ? 1 : -1;
  return a.localeCompare(b, undefined, { numeric: true });
}

/** Lowest internal_code among all procedures in this node's subtree (memoized). */
function minInternalCode(node: GroupNode, memo = new Map<GroupNode, string>()): string {
  const cached = memo.get(node);
  if (cached !== undefined) return cached;

  let min = '';
  for (const procedure of node.procedures) {
    const code = procedure.internal_code ?? '';
    if (code && (min === '' || compareInternalCode(code, min) < 0)) min = code;
  }
  for (const child of node.children.values()) {
    const code = minInternalCode(child, memo);
    if (code && (min === '' || compareInternalCode(code, min) < 0)) min = code;
  }

  memo.set(node, min);
  return min;
}

function toTreeNode(node: GroupNode): TreeNode<ProcedureTreeNodeData> {
  const childNodes = Array.from(node.children.values())
    .sort((a, b) => compareInternalCode(minInternalCode(a), minInternalCode(b)))
    .map(toTreeNode);
  const sortedProcedures = [...node.procedures].sort((a, b) =>
    compareInternalCode(a.internal_code ?? '', b.internal_code ?? '')
  );
  const leafNodes = sortedProcedures.map((procedure): TreeNode<ProcedureTreeNodeData> => ({
    key: `p-${procedure.id}`,
    label: procedure.description,
    leaf: true,
    data: { levelType: 'procedure', label: procedure.description, procedureCount: 1, procedure },
  }));
  const children = [...childNodes, ...leafNodes];
  const procedureCount = children.reduce((sum, child) => sum + (child.data?.procedureCount ?? 0), 0);

  return {
    key: node.key,
    label: node.label,
    leaf: false,
    expanded: false,
    children,
    data: { levelType: node.levelType, label: node.label, procedureCount },
  };
}

export function buildProceduresTree(rows: Procedure[]): TreeNode<ProcedureTreeNodeData>[] {
  const root: GroupNode = { key: 'root', levelType: 'family', label: '', children: new Map(), procedures: [] };

  for (const procedure of rows) {
    const familyNode = getOrCreateChild(root, procedure.family, 'family', procedure.family);
    const subfamilyNode = getOrCreateChild(familyNode, procedure.subfamily, 'subfamily', procedure.subfamily);

    if (!procedure.group) {
      subfamilyNode.procedures.push(procedure);
      continue;
    }
    const groupNode = getOrCreateChild(subfamilyNode, procedure.group, 'group', procedure.group);

    if (!procedure.subgroup) {
      groupNode.procedures.push(procedure);
      continue;
    }
    const subgroupNode = getOrCreateChild(groupNode, procedure.subgroup, 'subgroup', procedure.subgroup);
    subgroupNode.procedures.push(procedure);
  }

  return Array.from(root.children.values())
    .sort((a, b) => compareInternalCode(minInternalCode(a), minInternalCode(b)))
    .map(toTreeNode);
}

/**
 * Expand/collapse a group node from a click on its LABEL.
 *
 * Why this is needed at all: every consumer of this tree marks group nodes
 * `selectable = false` so only procedures can be picked. PrimeNG's
 * Tree.onNodeClick() returns immediately for a non-selectable node, which means
 * (onNodeSelect) never fires for a group and clicking its name is a complete
 * no-op - only the chevron, a ~16px target, does anything. So the toggle cannot
 * be hooked onto the selection events; it has to be a click handler on our own
 * label, rendered through <ng-template pTemplate="default">.
 *
 * Leaves are left alone: their click belongs to selection, which still works
 * exactly as before.
 *
 * Mutating `expanded` is all PrimeNG's own toggler does (see UITreeNode.expand
 * /.collapse) - the click marks the ancestor views dirty, so the row re-renders
 * without reassigning the tree, which would reset filter and selection state.
 *
 * @returns true if a group was toggled, false for a leaf/missing node, so the
 *          caller can decide whether to run change detection.
 */
export function toggleProcedureTreeNode(
  node: TreeNode<ProcedureTreeNodeData> | null | undefined,
  event?: Event,
): boolean {
  if (!node || node.leaf) return false;
  node.expanded = !node.expanded;
  // Belt and braces: groups are non-selectable today so the click would be
  // ignored upstream anyway, but stopping it here keeps the label's meaning
  // independent of whether someone later makes groups selectable.
  event?.stopPropagation();
  return true;
}
